import {
  BadgeDollarSign,
  Bot,
  CircleAlert,
  CircleCheck,
  Percent,
  RefreshCw,
  Scale,
  WalletCards,
} from "lucide-react";
import { PageHeader } from "@tribunalea/ui";
import { useEffect, useMemo, useState } from "react";

const ars = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});
const usd = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const integer = new Intl.NumberFormat("es-AR");
const percentage = new Intl.NumberFormat("es-AR", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const dateTime = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function currentMonth() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Mendoza",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(
    parts.filter(({ type }) => type !== "literal")
      .map(({ type, value }) => [type, value]),
  );
  return `${values.year}-${values.month}`;
}

function minorUnits(value) {
  return Number(value || 0) / 100;
}

function usdMicros(value) {
  return Number(value || 0) / 1_000_000;
}

function providerState(provider, readyLabel) {
  if (provider?.status === "ready" && provider?.message) {
    return { label: `${readyLabel} parcialmente`, tone: "warning", Icon: CircleAlert };
  }
  if (provider?.status === "ready") {
    return { label: readyLabel, tone: "ready", Icon: CircleCheck };
  }
  if (provider?.status === "not_configured") {
    return { label: "Sin configurar", tone: "warning", Icon: CircleAlert };
  }
  return { label: "No disponible", tone: "error", Icon: CircleAlert };
}

function ProviderStatus({ provider, readyLabel }) {
  const state = providerState(provider, readyLabel);
  const StatusIcon = state.Icon;
  return (
    <div className={`financial-provider-status financial-provider-status--${state.tone}`}>
      <StatusIcon size={16} />
      <div>
        <strong>{state.label}</strong>
        {provider?.message ? <span>{provider.message}</span> : null}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, detail, tone = "default" }) {
  return (
    <article className={`financial-metric financial-metric--${tone}`}>
      <span><Icon size={18} /></span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

function IncomeBreakdown({ items = [] }) {
  const labels = {
    one_time: "Compras únicas",
    subscription: "Suscripciones",
  };
  return (
    <div className="financial-table-wrap">
      <table className="financial-table">
        <thead>
          <tr>
            <th>Origen</th>
            <th>Pagos</th>
            <th>Bruto</th>
            <th>Cargos</th>
            <th>Neto</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.kind}>
              <td>{labels[item.kind] || item.kind}</td>
              <td>{integer.format(item.payments || 0)}</td>
              <td>{ars.format(minorUnits(item.grossMinorUnits))}</td>
              <td>{ars.format(minorUnits(
                Number(item.refundedMinorUnits || 0)
                + Number(item.chargedBackMinorUnits || 0)
                + Number(item.feesAndTaxesMinorUnits || 0),
              ))}</td>
              <td><strong>{ars.format(minorUnits(item.netMinorUnits))}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostBreakdown({ items = [] }) {
  return (
    <div className="financial-table-wrap">
      <table className="financial-table">
        <thead>
          <tr><th>Concepto OpenAI</th><th>Cantidad</th><th>Costo</th></tr>
        </thead>
        <tbody>
          {items.length ? items.map((item) => (
            <tr key={`${item.currency}:${item.lineItem}`}>
              <td>{item.lineItem}</td>
              <td>{item.quantity
                ? `${integer.format(item.quantity)}${item.quantityUnit ? ` ${item.quantityUnit}` : ""}`
                : "—"}</td>
              <td><strong>{usd.format(usdMicros(item.amountMicros))}</strong></td>
            </tr>
          )) : (
            <tr><td colSpan="3" className="financial-empty">Sin costos para este período.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function FinancialReportAdminPage({ api }) {
  const [month, setMonth] = useState(currentMonth);
  const [report, setReport] = useState(null);
  const [exchangeRate, setExchangeRate] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  async function load({ refresh = false } = {}) {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      setReport(await api.monthlyFinancialReport(month, { refresh }));
      setError("");
    } catch (requestError) {
      setError(requestError.message || "No pudimos cargar el reporte financiero.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { void load(); }, [month]);

  const income = report?.income?.mercadoPago;
  const officialCosts = report?.costs?.openAi;
  const localCosts = report?.costs?.localOpenAi;
  const incomeReady = income?.status === "ready";
  const openAiReady = officialCosts?.status === "ready";
  const incomeTotals = income?.totals || {};
  const officialUsdMicros = useMemo(() => (
    officialCosts?.totals?.find(({ currency }) => currency === "USD")?.amountMicros || 0
  ), [officialCosts]);
  const deductions = Number(incomeTotals.refundedMinorUnits || 0)
    + Number(incomeTotals.chargedBackMinorUnits || 0)
    + Number(incomeTotals.feesAndTaxesMinorUnits || 0);
  const parsedExchangeRate = Number(exchangeRate);
  const openAiCostArs = parsedExchangeRate > 0
    ? usdMicros(officialUsdMicros) * parsedExchangeRate
    : null;
  const netIncomeArs = minorUnits(incomeTotals.netMinorUnits);
  const margin = openAiCostArs === null || !incomeReady || !openAiReady
    ? null
    : netIncomeArs - openAiCostArs;
  const costIncomePercentage = margin === null || netIncomeArs <= 0
    ? null
    : openAiCostArs / netIncomeArs;
  const profitPercentage = margin === null || netIncomeArs <= 0
    ? null
    : margin / netIncomeArs;
  const reconciliationDifference = openAiReady
    && localCosts?.status === "ready"
    ? officialUsdMicros - Number(localCosts.amountMicros || 0)
    : null;

  return (
    <div className="financial-report-page">
      <PageHeader
        hero
        eyebrow="Administración · Finanzas"
        title="Ingresos y costos"
        description="Cobros netos de Mercado Pago y costos oficiales de OpenAI, conciliados por mes."
      />

      <section className="financial-report-toolbar">
        <label>
          Mes
          <input
            type="month"
            value={month}
            max={currentMonth()}
            onChange={(event) => setMonth(event.target.value)}
          />
        </label>
        <label>
          Tipo de cambio USD → ARS <small>opcional</small>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="Ej. 1500"
            value={exchangeRate}
            onChange={(event) => setExchangeRate(event.target.value)}
          />
        </label>
        <button type="button" onClick={() => load({ refresh: true })} disabled={loading || refreshing}>
          <RefreshCw size={16} className={refreshing ? "doctrine-spin" : ""} />
          {refreshing ? "Consultando…" : "Actualizar proveedores"}
        </button>
      </section>

      {error ? <div className="support-access-error" role="alert">{error}</div> : null}
      {loading && !report ? <div className="support-access-state">Cargando reporte mensual…</div> : null}

      {report ? (
        <>
          <div className="financial-provider-grid">
            <ProviderStatus provider={income} readyLabel="Mercado Pago conciliado" />
            <ProviderStatus provider={officialCosts} readyLabel="OpenAI conciliado" />
            <div className="financial-cache-status">
              <span>Última actualización</span>
              <strong>{report.cache?.refreshedAt
                ? dateTime.format(new Date(report.cache.refreshedAt))
                : "—"}</strong>
              <small>{report.cache?.source === "cache" ? "Datos almacenados" : "Consulta a proveedores"}</small>
            </div>
          </div>

          {report.cache?.stale ? (
            <div className="financial-warning" role="status">
              <CircleAlert size={17} /> Se muestran los últimos datos disponibles porque falló la actualización.
            </div>
          ) : null}

          <section className="financial-metrics">
            <MetricCard
              icon={BadgeDollarSign}
              label="Ingreso bruto"
              value={incomeReady ? ars.format(minorUnits(incomeTotals.grossMinorUnits)) : "—"}
              detail={incomeReady
                ? `${integer.format(incomeTotals.payments || 0)} cobros de Tribunalea`
                : "Mercado Pago no disponible"}
            />
            <MetricCard
              icon={Scale}
              label="Devoluciones y cargos"
              value={incomeReady ? ars.format(minorUnits(deductions)) : "—"}
              detail="Reembolsos, contracargos, comisiones e impuestos"
              tone="negative"
            />
            <MetricCard
              icon={WalletCards}
              label="Ingreso neto"
              value={incomeReady ? ars.format(minorUnits(incomeTotals.netMinorUnits)) : "—"}
              detail="Neto informado por Mercado Pago"
              tone="positive"
            />
            <MetricCard
              icon={Bot}
              label="Costo OpenAI"
              value={openAiReady ? usd.format(usdMicros(officialUsdMicros)) : "—"}
              detail={openAiReady
                ? "Costo oficial de la organización"
                : "OpenAI administrativo no disponible"}
            />
            <MetricCard
              icon={Scale}
              label="Margen directo"
              value={margin === null ? "—" : ars.format(margin)}
              detail={!incomeReady || !openAiReady
                ? "Requiere ambos proveedores conciliados"
                : margin === null
                  ? "Ingresá un tipo de cambio para calcularlo"
                  : `OpenAI convertido a ${ars.format(parsedExchangeRate)} por USD`}
              tone={margin !== null && margin < 0 ? "negative" : "positive"}
            />
            <MetricCard
              icon={Percent}
              label="Costo / ingreso"
              value={costIncomePercentage === null ? "—" : percentage.format(costIncomePercentage)}
              detail="Costo OpenAI sobre ingreso neto"
              tone="negative"
            />
            <MetricCard
              icon={Percent}
              label="Margen de ganancia"
              value={profitPercentage === null ? "—" : percentage.format(profitPercentage)}
              detail="Ganancia directa sobre ingreso neto"
              tone={profitPercentage !== null && profitPercentage < 0 ? "negative" : "positive"}
            />
          </section>

          <div className="financial-report-columns">
            <section className="financial-report-panel">
              <header>
                <div><span>INGRESOS</span><h2>Mercado Pago</h2></div>
                <small>Por fecha de aprobación</small>
              </header>
              {income?.status === "ready"
                ? <IncomeBreakdown items={income.breakdown} />
                : <p className="financial-empty">{income?.message || "No hay datos disponibles."}</p>}
              {income?.ignoredPayments ? (
                <p className="financial-note">
                  Se ignoraron {integer.format(income.ignoredPayments)} pagos sin una referencia de Tribunalea.
                </p>
              ) : null}
            </section>

            <section className="financial-report-panel">
              <header>
                <div><span>COSTOS</span><h2>OpenAI</h2></div>
                <small>{officialCosts?.scope === "projects"
                  ? `${integer.format(officialCosts.projectIds?.length || 0)} ${officialCosts.projectIds?.length === 1 ? "proyecto" : "proyectos"} de Tribunalea`
                  : "Proyectos de Tribunalea"}</small>
              </header>
              {officialCosts?.status === "ready"
                ? <CostBreakdown items={officialCosts.lineItems} />
                : <p className="financial-empty">{officialCosts?.message || "No hay datos disponibles."}</p>}
            </section>
          </div>

          <section className="financial-report-panel financial-reconciliation">
            <header>
              <div><span>CONTROL</span><h2>Conciliación OpenAI</h2></div>
              <small>Proveedor vs. eventos internos</small>
            </header>
            <div>
              <article><span>Costo oficial</span><strong>{openAiReady ? usd.format(usdMicros(officialUsdMicros)) : "—"}</strong></article>
              <article><span>Atribuido localmente</span><strong>{localCosts?.status === "ready" ? usd.format(usdMicros(localCosts.amountMicros)) : "—"}</strong></article>
              <article><span>Diferencia sin atribuir</span><strong>{reconciliationDifference === null ? "—" : usd.format(usdMicros(reconciliationDifference))}</strong></article>
            </div>
            <p>Una diferencia puede corresponder a jobs internos, otras API keys, almacenamiento o consumos todavía no registrados por la aplicación.</p>
          </section>
        </>
      ) : null}
    </div>
  );
}
